import React, { createContext, useContext, useState, useCallback } from 'react';
import { LangCode, LANGUAGES, LANG_BY_CODE } from './languages';
import en from './locales/en';
import es from './locales/es';

// Lightweight, dependency-free i18n — in the spirit of the rest of this codebase
// (e.g. the hand-rolled icon set). Catalogs are plain objects; a missing key in
// the active language falls back to English, then to the key itself, so nothing
// ever renders blank.
//
// Two layers of translation:
//  1. UI strings — via the t() / tm() helpers below and per-language catalogs.
//  2. AI responses — via aiLanguageDirective(), appended to every prompt in
//     api.ts. The model is natively multilingual, so this works for every
//     language in LANGUAGES the moment it is selected, even before its UI
//     catalog exists.

const CATALOGS: Partial<Record<LangCode, any>> = { en, es };
const STORAGE_KEY = 'dms_lang';

function detectInitial(): LangCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as LangCode | null;
    if (saved && LANG_BY_CODE[saved]) return saved;
  } catch { /* private mode / blocked storage */ }
  try {
    const nav = (navigator.language || 'en').slice(0, 2).toLowerCase() as LangCode;
    if (LANG_BY_CODE[nav]) return nav;
  } catch { /* no navigator */ }
  return 'en';
}

// Module-level mirror of the active language so non-React code (api.ts) can read
// it synchronously without threading the value through every call site.
let activeLang: LangCode = detectInitial();

export function getActiveLang(): LangCode { return activeLang; }

// The instruction appended to every AI system prompt. Empty for English (the
// prompts are already written in English). For any other language it tells the
// model to answer in that language, warmly and naturally for a classroom teacher.
export function aiLanguageDirective(): string {
  if (activeLang === 'en') return '';
  const meta = LANG_BY_CODE[activeLang];
  if (!meta) return '';
  return `\n\nRespond entirely in ${meta.english}. Write warm, natural, plain ${meta.english} appropriate for a classroom teacher — not a literal translation. Keep any proper names as given.`;
}

function applyDocument(code: LangCode) {
  try {
    const meta = LANG_BY_CODE[code];
    document.documentElement.lang = code;
    document.documentElement.dir = meta?.dir || 'ltr';
  } catch { /* SSR / no document */ }
}
applyDocument(activeLang);

// Resolve a dot-path ("landing.heroTitle") against a catalog object.
function lookup(cat: any, path: string): any {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), cat);
}

interface I18nValue {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  // Translate to a string. `vars` fills {placeholders}. Falls back en → key.
  t: (path: string, vars?: Record<string, string | number>) => string;
  // Translate to raw value (arrays/objects), e.g. landing.features. Falls back en.
  tm: <T = any>(path: string) => T;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(activeLang);

  const setLang = useCallback((code: LangCode) => {
    if (!LANG_BY_CODE[code]) return;
    activeLang = code;
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    applyDocument(code);
    setLangState(code);
  }, []);

  const t = useCallback((path: string, vars?: Record<string, string | number>) => {
    const cat = CATALOGS[lang] || en;
    let val = lookup(cat, path);
    if (typeof val !== 'string') val = lookup(en, path);
    if (typeof val !== 'string') return path;
    if (vars) {
      for (const k of Object.keys(vars)) val = (val as string).replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
    }
    return val as string;
  }, [lang]);

  const tm = useCallback(<T = any>(path: string): T => {
    const cat = CATALOGS[lang] || en;
    const val = lookup(cat, path);
    return (val === undefined ? lookup(en, path) : val) as T;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t, tm }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
  return ctx;
}

export { LANGUAGES };
export type { LangCode };
